// Product artwork for a DeviceType — stored as a URL in DeviceType.ImageLocation.
//
// ⚠️ WHY ImageLocation AND NOT ImageId. DeviceType has both: ImageId is an FK
// into the ImageData table (a managed library), ImageLocation is a plain URL on
// the row. We use the URL because the requirement is only "show a picture, share
// one file across products, change/remove it" — and a shared URL string gives
// that with no ImageData rows to create, dedupe or garbage-collect. Two products
// that look alike simply carry the SAME ImageLocation. ImageLocation is already
// in DeviceTypesScreen's write whitelist, so no schema or endpoint changes.
//
// Upload reuses the firmware upload route (`<host>/upload`, a sibling of /api):
// it stores by file extension, so `plug.png` lands in `/uploads/png/plug.png`
// and is served back as an absolute URL — exactly what an <img src> needs.
// See core/devices/otaBinFiles.js for the route's quirks (the Content-Type trap
// in particular).

import axios from 'axios';
import { UPLOAD_URL } from './otaBinFiles';
import { updateDeviceType } from '../api/api';

// What the upload route will serve back as an image. SVG is allowed but note it
// is rendered as a document — only upload SVGs you trust.
export const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg'];

// A product photo is not a firmware blob; keep it small so the list stays quick
// to load (every distinct image is fetched for the gallery). 4 MB is generous.
export const MAX_IMAGE_BYTES = 4 * 1024 * 1024;

const extOf = (name) => String(name || '').split('.').pop().toLowerCase();

/**
 * Upload one image file and return the public URL the server stored it at.
 * Validates type and size first so a wrong file fails HERE, before the row is
 * touched. Does NOT write any DeviceType — the caller decides which types point
 * at the returned URL (that is how one image is shared across products).
 */
export async function uploadDeviceTypeImage(file, { onProgress, signal } = {}) {
  if (!file) throw new Error('No file chosen.');
  const ext = extOf(file.name);
  if (!IMAGE_EXTS.includes(ext)) {
    throw new Error(`Not an image: .${ext}. Use ${IMAGE_EXTS.join(', ')}.`);
  }
  if (file.size > MAX_IMAGE_BYTES) {
    throw new Error(`Image is ${(file.size / 1048576).toFixed(1)} MB — the limit is 4 MB.`);
  }

  const form = new FormData();
  // Field name `file`, and NO Content-Type header — the browser writes the
  // multipart boundary; setting the header by hand drops it and the server sees
  // no file while still answering 200. (Same trap as uploadBinFile.)
  form.append('file', file, file.name);

  const res = await axios.post(UPLOAD_URL, form, {
    signal,
    timeout: 120000,
    onUploadProgress: (e) => {
      if (!onProgress) return;
      onProgress(e.total ? Math.round((e.loaded / e.total) * 100) : null);
    },
  });

  const url = res && res.data && (res.data.url || res.data.Url);
  if (!url) {
    const m = (res && res.data && res.data.message) || 'the server returned no URL.';
    throw new Error(`Upload failed: ${m}`);
  }
  return String(url);
}

/**
 * Point a DeviceType at an image URL (or clear it with '').
 * One row, keyed by DeviceID — the same where-clause the screen's own save uses.
 */
export const setDeviceTypeImage = (deviceId, url) =>
  updateDeviceType({ ImageLocation: url || '' }, `DeviceID = ${Number(deviceId)}`);

export const clearDeviceTypeImage = (deviceId) => setDeviceTypeImage(deviceId, '');

/**
 * The distinct images already used across the catalog, each with the product
 * names that share it — this is the "choose an existing image" gallery, and the
 * whole mechanism by which one file is reused for many products. No endpoint:
 * it is derived from the rows already loaded for the list.
 */
export function catalogImages(rows) {
  const by = new Map();
  (rows || []).forEach((r) => {
    const url = String(r.ImageLocation || '').trim();
    if (!url) return;
    if (!by.has(url)) by.set(url, []);
    by.get(url).push(r.DeviceName || `#${r.DeviceID}`);
  });
  return Array.from(by.entries())
    .map(([url, names]) => ({ url, names }))
    .sort((a, b) => b.names.length - a.names.length);
}
